import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import ScheduleManager from "../../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import schedule from "../../../components/CustomComponents/TimeSchedule/schedule.js";
import CommonTimeSlotForAllWeek from "../../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import { checkIfCommonTimeSlots, convertToScheduleFormat } from "../../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import swal from "sweetalert";
import moment from "moment";

function UpdateDiningArea() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { sA_ID } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Time Scheduling States
  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);

  const [formData, setFormData] = useState();

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const fetchDiningAreaData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/ServiceArea/GetServiceAreaByID`,
        {
          params: { CompID, ID: sA_ID },
        }
      );

      const areaData = response.data.data || response.data;
      // Dining Area Data logged

      if (
        !areaData.serviceAreaTables ||
        areaData.serviceAreaTables.length === 0
      ) {
        areaData.serviceAreaTables = [
          {
            t_ID: 0,
            localizeID: "en",
            t_Name: "",
            t_NoOfSeats: "",
            t_Shape: "",
            t_Status: "Open",
            t_Publish: true,
            t_Remarks: "",
            t_Xaxis: 0,
            t_Yaxis: 0,
            t_Version: 1,
            updatedateTime: new Date().toISOString(),
            compID: CompID,
            sA_ID: sA_ID,
            isTableMerged: false,
            mergedTableID: null,
            t_OriginalStatus: "Available",
          },
        ];
      } else {
        // Set t_OriginalStatus for each table row from DB
        areaData.serviceAreaTables = areaData.serviceAreaTables.map((table) => ({
          ...table,
          t_OriginalStatus: table.t_Status,
        }));
      }

      setFormData(areaData);
      
      // Load time scheduling data if available
      if (areaData.serviceTimeDetails && Array.isArray(areaData.serviceTimeDetails) && areaData.serviceTimeDetails.length > 0) {
        setRegularHours(false);
        const isCommon = checkIfCommonTimeSlots(areaData.serviceTimeDetails);
        if (isCommon) {
          const firstDaySlots = areaData.serviceTimeDetails
            .filter((day) => day.dayName === areaData.serviceTimeDetails[0].dayName)
            .map((day) => ({
              from: day.openTime,
              to: day.closeTime,
            }));
          setCommonTimeSlot(firstDaySlots);
        } else {
          setUseSameTimeSlots(false);
        }
        const convertedSchedules = convertToScheduleFormat(areaData.serviceTimeDetails);
        setSchedules(convertedSchedules);
      } else {
        setRegularHours(true);
        setSchedules(schedule);
        setCommonTimeSlot([{ from: "00:00", to: "23:59" }]);
      }
    } catch (error) {
      // Error fetching dining area data silently
      showErrorToast("Failed to load dining area data");
    } finally {
      setLoading(false);
    }
  }, [CompID, sA_ID]);


  useEffect(() => {
    if (CompID) {
      fetchDiningAreaData();
    }
  }, [CompID, fetchDiningAreaData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTableChange = (index, field, value) => {
    const updatedTables = [...formData?.serviceAreaTables];
    updatedTables[index] = { ...updatedTables[index], [field]: value };
    setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
  };

  const handleTableToggleStatus = (index) => {
    const updatedTables = [...formData?.serviceAreaTables];
    updatedTables[index] = {
      ...updatedTables[index],
      t_Publish: !updatedTables[index].t_Publish,
    };
    setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
  };

  const addTableRow = () => {
    setFormData((prev) => ({
      ...prev,
      serviceAreaTables: [
        ...prev.serviceAreaTables,
        {
          t_ID: 0,
          localizeID: "en",
          t_Name: "",
          t_NoOfSeats: "",
          t_Shape: "",
          t_Status: "Available",
          t_Publish: true,
          t_Xaxis: 0,
          t_Yaxis: 0,
          t_Remarks: "",
          t_Version: 1,
          updatedateTime: new Date().toISOString(),
          compID: CompID,
          sA_ID: sA_ID,
          isTableMerged: false,
          mergedTableID: null,
          angel: 0, // Default angle for new tables
          t_OriginalStatus: "Available",
        },
      ],
    }));
  };

  const removeTableRow = async (table, index) => {
    if (formData?.serviceAreaTables.length === 1) {
      showErrorToast("At least one table is required");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${
          table.t_Name || "this table"
        }?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      if (table.t_ID > 0) {
        const response = await axiosInstance.delete(
          "/api/ServiceAreaTable/DeleteServiceAreaTable",
          {
            params: {
              ID: table.t_ID,
            },
          }
        );

        if (response.data.status) {
          showSuccessToast("Table deleted successfully");
          setFormData((prev) => ({
            ...prev,
            serviceAreaTables: prev.serviceAreaTables.filter(
              (_, i) => i !== index
            ),
          }));
        } else {
          showErrorToast(response.data.message || "Deletion failed");
        }
      } else {
        // Remove locally for new tables (t_ID: 0)
        setFormData((prev) => ({
          ...prev,
          serviceAreaTables: prev.serviceAreaTables.filter(
            (_, i) => i !== index
          ),
        }));
        showSuccessToast("Table removed successfully");
      }
    } catch (err) {
      // Error deleting table silently
      showErrorToast("Failed to delete table");
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData?.sA_Name || formData?.sA_Name.trim().length === 0) {
        showErrorToast("Please enter a valid dining area name");
        return;
      }

      const validTables = formData?.serviceAreaTables.filter(
        (table) =>
          table.t_Name?.trim().length > 0 &&
          table.t_NoOfSeats &&
          !isNaN(table.t_NoOfSeats) &&
          parseInt(table.t_NoOfSeats) > 0
      );

      if (validTables.length === 0) {
        showErrorToast("At least one valid table is required");
        return;
      }

      for (const table of validTables) {
        if (!table.t_Name || table.t_Name.trim().length === 0) {
          showErrorToast("Please enter a valid table name for all tables");
          return;
        }
        if (
          !table.t_NoOfSeats ||
          isNaN(table.t_NoOfSeats) ||
          parseInt(table.t_NoOfSeats) <= 0
        ) {
          showErrorToast("Please enter a valid number of seats for all tables");
          return;
        }
      }

      setLoading(true);
      const now = new Date().toISOString();

      // Calculate number of tables and total seats
      const numberOfTables = validTables.length;
      const numberOfSeats = validTables.reduce((total, table) => {
        return total + (parseInt(table.t_NoOfSeats) || 0);
      }, 0);

      // Format time schedules for ServiceTimeDetail
      const formattedTimeDetails = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedTimeDetails.push({
                  ID: 0,
                  CompID: CompID,
                  ST_ID: parseInt(sA_ID), // Service Area ID
                  OpenTime: moment(slot.from, ["h:mm A", "HH:mm"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A", "HH:mm"]).format("HH:mm"),
                  DayName: day,
                  DateTime: now,
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A", "HH:mm"]).isValid() &&
                  moment(slot.to, ["h:mm A", "HH:mm"]).isValid()
                ) {
                  formattedTimeDetails.push({
                    ID: 0,
                    CompID: CompID,
                    ST_ID: parseInt(sA_ID), // Service Area ID
                    OpenTime: moment(slot.from, ["h:mm A", "HH:mm"]).format("HH:mm"),
                    CloseTime: moment(slot.to, ["h:mm A", "HH:mm"]).format("HH:mm"),
                    DayName: schedule.day,
                    DateTime: now,
                  });
                }
              });
            }
          });
        }
      }

      const payload = {
        CompID,
        ID: parseInt(sA_ID),
        sA_Name: formData?.sA_Name,
        sA_Detail: formData?.sA_Detail,
        sA_Status: formData?.sA_Status,
        sA_Width: formData?.sA_Width,
        sA_Height: formData?.sA_Height,
        sA_SortOrder: formData?.sA_SortOrder,
        updateDateTime: now,
        NumberofTables: numberOfTables,
        NumberofSeats: numberOfSeats,
        isAvailableInTimeIntervals: !regularHours,
        ServiceTimeDetails: formattedTimeDetails,
        serviceAreaTables: validTables.map((table) => ({
          ...table,
          angel: table.angel || 0, 
        })),
      };

      const response = await axiosInstance.put(
        "/api/ServiceArea/UpdateServiceArea",
        payload
      );

      if (response.data.status) {
        showSuccessToast("Dining area updated successfully");
        navigate("/diningAreas");
      } else {
        showErrorToast(response.data.message || "Failed to update dining area");
      }
    } catch (error) {
      // Update failed silently
      showErrorToast("Failed to update dining area");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/dining-areas">Dining Areas</Link>
            </li>
            <li className="breadcrumb-item active">Update Dining Area</li>
          </ol>
          <h1 className="page-header mb-0">Update Dining Area</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn px-4"
            disabled={!formData?.sA_Name?.trim()}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: (!formData?.sA_Name?.trim()) ? 'not-allowed' : 'pointer',
              opacity: (!formData?.sA_Name?.trim()) ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (formData?.sA_Name?.trim()) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (formData?.sA_Name?.trim()) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            Submit
          </button>
        </div>
      </div>
      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Update Dining Area</PanelHeader>
          <PanelBody>
            <div className="p-2">
              <div className="row">
                <div className="col-12 mb-3">
                  <FormInput
                    label="Dining Area Name"
                    placeHolder="Enter dining area name"
                    type="text"
                    name="sA_Name"
                    value={formData?.sA_Name}
                    onchange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="row d-none">
                <div className="col-6 mb-3">
                  <FormInput
                    label="Dining Area Width(px)"
                    placeHolder="Enter dining area Width"
                    type="number"
                    name="sA_Width"
                    step="0.01"
                    value={formData?.sA_Width || 0}
                    onchange={handleInputChange}
                    required
                  />
                </div>

                <div className="col-6 mb-3">
                  <FormInput
                    label="Dining Area Height(px)"
                    placeHolder="Enter dining area Height"
                    type="number"
                    name="sA_Height"
                    step="0.01"
                    value={formData?.sA_Height || 0}
                    onchange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="sA_Detail"
                    value={formData?.sA_Detail}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">Status</label>
                  <div className="d-flex align-items-center">
                    <ToggleSwitch
                      isChecked={formData?.sA_Status}
                      onToggle={() =>
                        setFormData((prev) => ({
                          ...prev,
                          sA_Status: !prev.sA_Status,
                        }))
                      }
                      label={formData?.sA_Status ? "On" : "Off"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Tables</label>
                    <div className="d-flex gap-3">
                      <span className="badge bg-info">
                        <i className="fa fa-table me-1"></i>
                        Tables: {formData?.serviceAreaTables?.filter(table => 
                          table.t_Name?.trim().length > 0 && 
                          table.t_NoOfSeats && 
                          !isNaN(table.t_NoOfSeats) && 
                          parseInt(table.t_NoOfSeats) > 0
                        ).length || 0}
                      </span>
                      <span className="badge bg-success">
                        <i className="fa fa-users me-1"></i>
                        Seats: {formData?.serviceAreaTables?.reduce((total, table) => {
                          return total + (parseInt(table.t_NoOfSeats) || 0);
                        }, 0) || 0}
                      </span>
                    </div>
                  </div>
                  <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Table Name</th>
                      <th>Number of Seats</th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Shape
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Status
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Publish
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.serviceAreaTables.map((table, index) => (
                      <tr key={table.t_ID || `draft-${index}`}>
                        {(() => {
                          const isEditable = table.t_OriginalStatus === "Available";
                          return (
                            <>
                              <td>
                                <FormInput
                                  placeHolder="Enter table name"
                                  type="text"
                                  value={table.t_Name}
                                  onchange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_Name",
                                      e.target.value
                                    )
                                  }
                                  required
                                  readOnly={!isEditable}
                                />
                              </td>
                              <td>
                                <FormInput
                                  placeHolder="Enter number of seats"
                                  type="number"
                                  value={table.t_NoOfSeats}
                                  onchange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_NoOfSeats",
                                      e.target.value
                                    )
                                  }
                                  required
                                  step="1"
                                  min="1"
                                  readOnly={!isEditable}
                                />
                              </td>
                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  {["Square", "Rectangle", "Round"].map((shape) => (
                                    <button
                                      key={shape}
                                      type="button"
                                      className={`btn btn-sm ${table.t_Shape === shape
                                          ? "btn-primary"
                                          : "btn-outline-secondary"
                                        }`}
                                      onClick={() => handleTableChange(index, "t_Shape", shape)}
                                      disabled={!isEditable}
                                      style={{
                                        width: "60px",
                                        height: "40px",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "12px",
                                        fontWeight: "500"
                                      }}
                                      title={shape}
                                    >
                                      {shape === "Square" && (
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                          }}
                                        />
                                      )}
                                      {shape === "Rectangle" && (
                                        <div
                                          style={{
                                            width: "24px",
                                            height: "16px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                          }}
                                        />
                                      )}
                                      {shape === "Round" && (
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            borderRadius: "50%"
                                          }}
                                        />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <select
                                  className="form-control"
                                  disabled={!isEditable}
                                  style={{
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                                  value={table.t_Status || ""}
                                  onChange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_Status",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Status</option>
                                  <option value="Available">Available</option>
                                  <option value="Dirty">Dirty</option>
                                  <option value="Seated">Seated</option>
                                  <option value="Reserved">Reserved</option>
                                  <option value="Cancel">Cancel</option>
                                  <option value="Block">Block</option>
                                </select>
                              </td>
                              <td className="d-flex justify-content-center">
                                <ToggleSwitch
                                  isChecked={table.t_Publish}
                                  onToggle={() =>
                                    handleTableToggleStatus(index)
                                  }
                                  label={
                                    table.t_Publish ? "Active" : "Inactive"
                                  }
                                  disabled={!isEditable}
                                  themeColor={themeColor}
                                />
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                {index ===
                                formData?.serviceAreaTables.length - 1 ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm"
                                      onClick={addTableRow}
                                    >
                                      <i className="fa fa-plus"></i>
                                    </button>{" "}
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() =>
                                        removeTableRow(table, index)
                                      }
                                      disabled={!isEditable}
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => removeTableRow(table, index)}
                                    disabled={!isEditable}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                )}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              </div>

              {/* Time Scheduling Section */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div className="card p-3">
                    <h5 className="card-title mb-3">Time Scheduling</h5>
                    <div className="row g-3 d-flex align-items-center p-4">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => setRegularHours(!regularHours)}
                          label="Available in regular Business Hours"
                          name="regularHours"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                    
                    {!regularHours && (
                      <>
                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={() => {
                                setUseSameTimeSlots(!useSameTimeSlots);
                                if (!useSameTimeSlots) {
                                  applyCommonTimeSlotToAll();
                                }
                              }}
                              label="Use same time slots for all days"
                              name="useSameTimeSlots"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <hr />

                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={setCommonTimeSlot}
                            commonTimeSlots={commonTimeSlot}
                            isProfilePage={false}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={setSchedules}
                            isProfilePage={false}
                            isDarkMode={isDarkMode}
                            themeColor={themeColor}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default UpdateDiningArea;
